/**
 * `GET /bql/schema`, parsed per entity and per field rather than
 * all-or-nothing.
 *
 * The old path (`parseResponse(BqlSchemaSchema, …)`) parsed the whole
 * descriptor atomically, so one operator the client's generated enum did not
 * yet know about threw the entire response — every entity, every field,
 * gone, at `shared/api/utils/contract.ts`. Additive drift (a new `Operator`,
 * a new `FieldType`) is the NORMAL way this contract grows, so that made the
 * normal case a total builder outage. This module trades that for: the one
 * entity, the one field, or the one `operator_legality` entry actually
 * carrying the unrecognised value is dropped, and every sibling still
 * renders.
 *
 * ## The envelope is loosened in exactly three places
 *
 * `entityEnvelopeSchema` and `bqlSchemaEnvelopeSchema` below are
 * `EntityDescriptorSchema`/`BqlSchemaSchema` with `fields`,
 * `operator_legality` and (per entity) `metric_legality` re-typed to
 * `unknown`/`Record<string, unknown>` so `parseArrayRows`/`parseRecordRows` can
 * re-gate them one row at a time. `metric_legality` is keyed by `FieldType`
 * exactly as `operator_legality` is, so it drifts the same way: a new type or a
 * new metric drops that one row, never the entity. A dropped row reads as "no
 * metric is legal on that type", which only hides measures, never offers one
 * the server refuses.
 * Nothing else on either schema is loosened: `pagination`, `projections`,
 * `required_permission` and the rest stay exactly as generated-strict as
 * `EntityDescriptorSchema` already makes them, because those are not additive
 * enum surfaces — a malformed one is this client's own model being wrong, not
 * the server growing, and a whole ENTITY failing on that ground is still the
 * right call (the entity-level `parseRecordRows` below still drops the whole
 * entity when its envelope itself does not parse).
 *
 * `operator_legality` is gated at BOTH the document level (this file's
 * `bqlSchemaEnvelopeSchema`) and every entity's own override
 * (`entityEnvelopeSchema`) — independently, because a per-entity override can
 * carry a `FieldType` the document-level table does not, and vice versa. See
 * `partial-contract.ts` for why `operator_legality` needs its KEY gated, not
 * only its value.
 *
 * A dropped entity needs no new UI: `field-catalog.ts`'s
 * `resolveEntityCatalog` already returns `undefined` for an entity the
 * descriptor never described, and `widget-card.tsx`'s `isEntityAbsent` branch
 * already renders that state — an entity dropped HERE is structurally
 * identical to one the server never sent. A dropped field is Sentry-only; see
 * the guardrail on `reportDroppedRowsOnce`.
 */
import { z } from 'zod'

import {
	parseArrayRows,
	parseRecordRows,
	rowKeyOf,
} from '@/shared/api/utils/partial-contract'

import { BqlContractError, parseResponse } from './contract'
import {
	EntityDescriptorSchema,
	FieldDescriptorSchema,
	FieldTypeSchema,
	MetricSchema,
	OperatorSchema,
} from './generated/schema'

import type {
	BqlSchema,
	EntityDescriptor,
	FieldType,
	Operator,
} from './generated/schema'
import type { DroppedRow } from '@/shared/api/utils/partial-contract'

/** `EntityDescriptorSchema`, with `fields` and both legality tables loosened for row-level re-gating. */
const entityEnvelopeSchema = EntityDescriptorSchema.extend({
	fields: z.array(z.unknown()),
	operator_legality: z.record(z.string(), z.unknown()).optional(),
	metric_legality: z.record(z.string(), z.unknown()).optional(),
})

/** One `metric_legality` row's value: the metrics a field type computes. */
const metricRowSchema = z.array(MetricSchema.shape.type)

type MetricLegality = NonNullable<EntityDescriptor['metric_legality']>

/** `BqlSchemaSchema`, with `entities`' values and the document's `operator_legality` loosened. */
const bqlSchemaEnvelopeSchema = z.object({
	entities: z.record(z.string(), z.unknown()),
	operator_legality: z.record(z.string(), z.unknown()).optional(),
})

/** A `FieldDescriptor`'s `id` when the row is at least shaped enough to have one, else its array index. */
const fieldRowKey = rowKeyOf('id')

/**
 * Gate one `operator_legality` map (document-level or a single entity's
 * override) key-by-key against `FieldTypeSchema` and value-by-value against
 * `z.array(OperatorSchema)`. An entry surviving both is kept under its own
 * key; either failure drops that one entry, named `${keyPrefix}.<key>`.
 */
const parseOperatorLegality = (
	raw: Record<string, unknown> | undefined,
	route: string,
	keyPrefix: string,
): PartialLegality => {
	if (!raw) return { value: {}, dropped: [] }

	const { value, dropped } = parseRecordRows(
		raw,
		z.array(OperatorSchema),
		(key, cause) => new BqlContractError(`${route} ${keyPrefix}.${key}`, cause),
		FieldTypeSchema,
	)

	return {
		value: value as Partial<Record<FieldType, Operator[]>>,
		dropped: prefixed(dropped, keyPrefix),
	}
}

/**
 * One entity's `metric_legality`, gated row by row the same way as
 * {@link parseOperatorLegality}: the key against `FieldTypeSchema`, the value
 * against the metric enum. Either failure drops that one row.
 */
const parseMetricLegality = (
	raw: Record<string, unknown> | undefined,
	route: string,
	keyPrefix: string,
): { value: MetricLegality; dropped: DroppedRow[] } => {
	if (!raw) return { value: {}, dropped: [] }

	const { value, dropped } = parseRecordRows(
		raw,
		metricRowSchema,
		(key, cause) => new BqlContractError(`${route} ${keyPrefix}.${key}`, cause),
		FieldTypeSchema,
	)

	return {
		value: value as MetricLegality,
		dropped: prefixed(dropped, keyPrefix),
	}
}

/**
 * `parseRecordRows`/`parseArrayRows` name a `DroppedRow` by its own bare key
 * (`geo_point`, `weird`, `broken`) — they have no notion of where in a
 * larger document that map or array sits. This re-homes each dropped row's
 * `key` under the caller's own position (`operator_legality.geo_point`,
 * `entities.cases.fields.weird`) so a Sentry report — and this file's own
 * tests — can tell two same-named rows in different entities apart.
 */
const prefixed = (dropped: DroppedRow[], prefix: string): DroppedRow[] =>
	dropped.map((row) => ({ ...row, key: `${prefix}.${row.key}` }))

type PartialLegality = {
	value: Partial<Record<FieldType, Operator[]>>
	dropped: DroppedRow[]
}

/** `undefined` when empty rather than `{}` — matches the generated schema's `.optional()`, never sent as an empty object on the wire. */
const undefinedIfEmpty = <T extends object>(value: T): T | undefined =>
	Object.keys(value).length > 0 ? value : undefined

/**
 * Parse `GET /bql/schema` with entity, field and `operator_legality`
 * blast radius: any one of those failing its own schema is dropped and
 * reported, everything else renders. See the module docblock for what stays
 * strict and why.
 */
export function parseBqlSchema(
	body: unknown,
	route: string,
): { value: BqlSchema; dropped: DroppedRow[] } {
	const envelope = parseResponse(bqlSchemaEnvelopeSchema, body, route)
	const dropped: DroppedRow[] = []

	const documentLegality = parseOperatorLegality(
		envelope.operator_legality,
		route,
		'operator_legality',
	)
	dropped.push(...documentLegality.dropped)

	const entitiesEnvelope = parseRecordRows(
		envelope.entities,
		entityEnvelopeSchema,
		(key, cause) => new BqlContractError(`${route} entities.${key}`, cause),
	)
	dropped.push(...prefixed(entitiesEnvelope.dropped, 'entities'))

	const entities: Record<string, EntityDescriptor> = {}

	for (const [name, entity] of Object.entries(entitiesEnvelope.value)) {
		const fields = parseArrayRows(
			entity.fields,
			FieldDescriptorSchema,
			(key, cause) =>
				new BqlContractError(`${route} entities.${name}.fields.${key}`, cause),
			fieldRowKey,
		)
		dropped.push(...prefixed(fields.dropped, `entities.${name}.fields`))

		const entityLegality = parseOperatorLegality(
			entity.operator_legality,
			route,
			`entities.${name}.operator_legality`,
		)
		dropped.push(...entityLegality.dropped)

		const metricLegality = parseMetricLegality(
			entity.metric_legality,
			route,
			`entities.${name}.metric_legality`,
		)
		dropped.push(...metricLegality.dropped)

		entities[name] = {
			...entity,
			fields: fields.value,
			operator_legality: undefinedIfEmpty(entityLegality.value),
			metric_legality: undefinedIfEmpty(metricLegality.value),
		}
	}

	return {
		value: {
			entities,
			operator_legality: undefinedIfEmpty(documentLegality.value),
		},
		dropped,
	}
}
